import React, { useState, useEffect } from 'react';
import {
  CheckCircle, XCircle, Activity, FileText, MessageSquare, Users,
  ChevronLeft, ChevronRight, ChevronDown, ChevronUp, RefreshCw,
  AlertTriangle, AlertCircle,
} from 'lucide-react';
import { useHealthMetrics, useWebhookLogs, useErrorMetrics, useSystemLogs } from '../../hooks/useAdminLogs';
import type { WebhookLog, SystemLog } from '../../hooks/useAdminLogs';

const PER_PAGE = 50;

// =====================================================
// SHARED HELPERS
// =====================================================

const formatRelativeTime = (dateStr: string) => {
  const diff = Date.now() - new Date(dateStr).getTime();
  const minutes = Math.floor(diff / 60000);
  if (minutes < 1) return 'agora';
  if (minutes < 60) return `${minutes}min atrás`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h atrás`;
  const days = Math.floor(hours / 24);
  return `${days}d atrás`;
};

const truncateUrl = (url: string, maxLen: number = 40) =>
  url.length > maxLen ? url.slice(0, maxLen) + '...' : url;

const truncateMessage = (msg: string, maxLen: number = 80) =>
  msg.length > maxLen ? msg.slice(0, maxLen) + '...' : msg;

// =====================================================
// WEBHOOK LOGS SECTION
// =====================================================

const STATUS_TABS = [
  { label: 'Todos', value: null },
  { label: 'Sucesso', value: 'success' },
  { label: 'Falha', value: 'failed' },
];

const statusBadge = (status: string) => {
  if (status === 'success') return <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-green-500/10 text-green-400">Sucesso</span>;
  if (status === 'failed') return <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-red-500/10 text-red-400">Falha</span>;
  return <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-500/10 text-yellow-400">Pendente</span>;
};

const statusCodeColor = (code: number | null) => {
  if (!code) return 'text-neutral-500';
  if (code >= 200 && code < 300) return 'text-green-400';
  if (code >= 400) return 'text-red-400';
  return 'text-yellow-400';
};

const successRateColor = (rate: number) => {
  if (rate >= 95) return 'text-green-400';
  if (rate >= 80) return 'text-yellow-400';
  return 'text-red-400';
};

const WebhookLogDetail: React.FC<{ log: WebhookLog }> = ({ log }) => (
  <tr>
    <td colSpan={7} className="p-4 bg-neutral-900/80 border-b border-white/5">
      <div className="grid grid-cols-1 gap-4 text-xs">
        {log.request_payload && (
          <div>
            <p className="text-neutral-500 font-medium mb-1">Request Payload</p>
            <pre className="bg-neutral-950 rounded-lg p-3 text-neutral-300 overflow-x-auto max-h-48">
              {JSON.stringify(log.request_payload, null, 2)}
            </pre>
          </div>
        )}
        {log.response_body && (
          <div>
            <p className="text-neutral-500 font-medium mb-1">Response Body</p>
            <pre className="bg-neutral-950 rounded-lg p-3 text-neutral-300 overflow-x-auto max-h-32">
              {log.response_body.slice(0, 2000)}
            </pre>
          </div>
        )}
        {log.error_message && (
          <div>
            <p className="text-neutral-500 font-medium mb-1">Error</p>
            <p className="text-red-400 bg-red-500/5 rounded-lg p-3">{log.error_message}</p>
          </div>
        )}
        <div className="flex gap-6 text-neutral-500">
          <span>Form: <span className="text-neutral-300">{log.form_title}</span></span>
          <span>User: <span className="text-neutral-300">{log.user_email}</span></span>
        </div>
      </div>
    </td>
  </tr>
);

const WebhookLogsSection: React.FC = () => {
  const { metrics, loading: metricsLoading, error: metricsError } = useHealthMetrics(60000);

  const [statusFilter, setStatusFilter] = useState<string | null>(null);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [page, setPage] = useState(1);
  const [expandedLogId, setExpandedLogId] = useState<string | null>(null);

  useEffect(() => { setPage(1); }, [statusFilter, dateFrom, dateTo]);

  const { logs, total, loading: logsLoading } = useWebhookLogs({
    status: statusFilter,
    dateFrom: dateFrom || null,
    dateTo: dateTo || null,
    page,
  });

  const totalPages = Math.ceil(total / PER_PAGE);

  const kpiCards = metrics
    ? [
        { label: 'Success Rate', value: `${metrics.webhook_success_rate}%`, icon: CheckCircle, color: successRateColor(metrics.webhook_success_rate) },
        { label: 'Webhooks / 24h', value: String(metrics.webhooks_last_24h), icon: Activity, color: 'text-blue-400' },
        { label: 'Erros / 24h', value: String(metrics.webhook_errors_last_24h), icon: XCircle, color: metrics.webhook_errors_last_24h > 0 ? 'text-red-400' : 'text-green-400' },
        { label: 'Forms Hoje', value: String(metrics.forms_created_today), icon: FileText, color: 'text-purple-400' },
        { label: 'Respostas Hoje', value: String(metrics.responses_today), icon: MessageSquare, color: 'text-cyan-400' },
        { label: 'Usuários Ativos', value: String(metrics.active_users_today), icon: Users, color: 'text-emerald-400' },
      ]
    : [];

  return (
    <>
      {metricsError && (
        <div className="mb-4 p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400 text-sm">
          Erro ao carregar métricas: {metricsError}
        </div>
      )}
      {metricsLoading ? (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="bg-neutral-900/50 border border-white/10 rounded-xl p-5 animate-pulse h-24" />
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
          {kpiCards.map(card => (
            <div key={card.label} className="bg-neutral-900/50 border border-white/10 rounded-xl p-5">
              <div className="flex items-center gap-2 mb-2">
                <card.icon className={`w-4 h-4 ${card.color}`} />
                <span className="text-xs text-neutral-500 uppercase tracking-wide">{card.label}</span>
              </div>
              <p className={`text-2xl font-bold ${card.color}`}>{card.value}</p>
            </div>
          ))}
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-4 mb-4">
        <div className="flex gap-1 overflow-x-auto whitespace-nowrap">
          {STATUS_TABS.map(tab => (
            <button
              key={tab.label}
              onClick={() => setStatusFilter(tab.value)}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                statusFilter === tab.value
                  ? 'bg-white/10 text-white'
                  : 'text-neutral-400 hover:text-white hover:bg-white/5'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
        <div className="flex flex-wrap items-center gap-2 ml-auto">
          <label className="text-xs text-neutral-500">De:</label>
          <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} className="bg-neutral-900 border border-white/10 rounded-lg px-3 py-1.5 text-xs text-white" />
          <label className="text-xs text-neutral-500">Até:</label>
          <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} className="bg-neutral-900 border border-white/10 rounded-lg px-3 py-1.5 text-xs text-white" />
          {(dateFrom || dateTo) && (
            <button onClick={() => { setDateFrom(''); setDateTo(''); }} className="text-xs text-neutral-500 hover:text-white">Limpar</button>
          )}
        </div>
      </div>

      {/* Webhook Logs Table */}
      <div tabIndex={0} className="bg-neutral-900/50 border border-white/10 rounded-xl overflow-x-auto">
        <table className="w-full min-w-[720px] text-sm">
          <thead>
            <tr className="border-b border-white/10 text-neutral-400 text-left">
              <th className="p-4 w-8" />
              <th className="p-4">Timestamp</th>
              <th className="p-4">Form</th>
              <th className="p-4">URL</th>
              <th className="p-4">Código</th>
              <th className="p-4">Status</th>
              <th className="p-4">Tentativa</th>
            </tr>
          </thead>
          <tbody>
            {logsLoading ? (
              <tr><td colSpan={7} className="p-8 text-center"><div className="w-6 h-6 border-2 border-primary-500 border-t-transparent rounded-full animate-spin mx-auto" /></td></tr>
            ) : logs.length === 0 ? (
              <tr><td colSpan={7} className="p-8 text-center text-neutral-500">Nenhum log encontrado</td></tr>
            ) : (
              logs.map(log => (
                <React.Fragment key={log.id}>
                  <tr onClick={() => setExpandedLogId(expandedLogId === log.id ? null : log.id)} className="border-b border-white/5 hover:bg-white/5 cursor-pointer transition-colors">
                    <td className="pl-4">{expandedLogId === log.id ? <ChevronUp className="w-4 h-4 text-neutral-500" /> : <ChevronDown className="w-4 h-4 text-neutral-500" />}</td>
                    <td className="p-4 text-neutral-400" title={new Date(log.created_at).toLocaleString('pt-BR')}>{formatRelativeTime(log.created_at)}</td>
                    <td className="p-4 text-white">{log.form_title}</td>
                    <td className="p-4 text-neutral-400 font-mono text-xs" title={log.webhook_url}>{truncateUrl(log.webhook_url)}</td>
                    <td className={`p-4 font-mono ${statusCodeColor(log.response_status_code)}`}>{log.response_status_code || '—'}</td>
                    <td className="p-4">{statusBadge(log.status)}</td>
                    <td className="p-4 text-neutral-400">#{log.attempt_number}</td>
                  </tr>
                  {expandedLogId === log.id && <WebhookLogDetail log={log} />}
                </React.Fragment>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {total > 0 && (
        <div className="flex items-center justify-between mt-4 text-sm text-neutral-400">
          <span>Mostrando {(page - 1) * PER_PAGE + 1}–{Math.min(page * PER_PAGE, total)} de {total}</span>
          <div className="flex gap-2">
            <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} className="p-1 rounded hover:bg-white/10 disabled:opacity-30"><ChevronLeft className="w-4 h-4" /></button>
            <span className="px-2">{page} / {totalPages}</span>
            <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages} className="p-1 rounded hover:bg-white/10 disabled:opacity-30"><ChevronRight className="w-4 h-4" /></button>
          </div>
        </div>
      )}
    </>
  );
};

// =====================================================
// SYSTEM LOGS SECTION
// =====================================================

const LEVEL_TABS = [
  { label: 'Todos', value: null },
  { label: 'Error', value: 'error' },
  { label: 'Warn', value: 'warn' },
  { label: 'Info', value: 'info' },
];

const levelBadge = (level: string) => {
  if (level === 'error') return <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-red-500/10 text-red-400">Error</span>;
  if (level === 'warn') return <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-500/10 text-yellow-400">Warn</span>;
  return <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-blue-500/10 text-blue-400">Info</span>;
};

const SystemLogDetail: React.FC<{ log: SystemLog }> = ({ log }) => (
  <tr>
    <td colSpan={6} className="p-4 bg-neutral-900/80 border-b border-white/5">
      <div className="grid grid-cols-1 gap-4 text-xs">
        {log.metadata && Object.keys(log.metadata).length > 0 && (
          <div>
            <p className="text-neutral-500 font-medium mb-1">Metadata</p>
            <pre className="bg-neutral-950 rounded-lg p-3 text-neutral-300 overflow-x-auto max-h-48">
              {JSON.stringify(log.metadata, null, 2)}
            </pre>
          </div>
        )}
        {log.user_agent && (
          <div>
            <p className="text-neutral-500 font-medium mb-1">User Agent</p>
            <p className="text-neutral-400 bg-neutral-950 rounded-lg p-3 break-all">{log.user_agent}</p>
          </div>
        )}
        {(log.metadata as Record<string, unknown>)?.stack && (
          <div>
            <p className="text-neutral-500 font-medium mb-1">Stack Trace</p>
            <pre className="bg-neutral-950 rounded-lg p-3 text-red-400 overflow-x-auto max-h-48">
              {String((log.metadata as Record<string, unknown>).stack)}
            </pre>
          </div>
        )}
        <div className="flex gap-6 text-neutral-500">
          <span>User ID: <span className="text-neutral-300 font-mono">{log.user_id || 'Anônimo'}</span></span>
          <span>Log ID: <span className="text-neutral-300 font-mono">{log.id}</span></span>
        </div>
      </div>
    </td>
  </tr>
);

const SystemLogsSection: React.FC = () => {
  const { metrics, loading: metricsLoading, error: metricsError } = useErrorMetrics(60000);

  const [levelFilter, setLevelFilter] = useState<string | null>(null);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [page, setPage] = useState(1);
  const [expandedLogId, setExpandedLogId] = useState<string | null>(null);

  useEffect(() => { setPage(1); }, [levelFilter, dateFrom, dateTo]);

  const { logs, total, loading: logsLoading } = useSystemLogs({
    level: levelFilter,
    dateFrom: dateFrom || null,
    dateTo: dateTo || null,
    page,
  });

  const totalPages = Math.ceil(total / PER_PAGE);

  const kpiCards = metrics
    ? [
        { label: 'Erros / 24h', value: String(metrics.errors_last_24h), icon: XCircle, color: metrics.errors_last_24h > 0 ? 'text-red-400' : 'text-green-400' },
        { label: 'Warns / 24h', value: String(metrics.warns_last_24h), icon: AlertTriangle, color: metrics.warns_last_24h > 0 ? 'text-yellow-400' : 'text-green-400' },
        { label: 'Total / 24h', value: String(metrics.total_last_24h), icon: Activity, color: 'text-blue-400' },
        { label: 'Top Categoria', value: metrics.top_categories?.[0]?.category || '—', icon: AlertCircle, color: 'text-purple-400' },
      ]
    : [];

  return (
    <>
      {metricsError && (
        <div className="mb-4 p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400 text-sm">
          Erro ao carregar métricas: {metricsError}
        </div>
      )}
      {metricsLoading ? (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="bg-neutral-900/50 border border-white/10 rounded-xl p-5 animate-pulse h-24" />
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          {kpiCards.map(card => (
            <div key={card.label} className="bg-neutral-900/50 border border-white/10 rounded-xl p-5">
              <div className="flex items-center gap-2 mb-2">
                <card.icon className={`w-4 h-4 ${card.color}`} />
                <span className="text-xs text-neutral-500 uppercase tracking-wide">{card.label}</span>
              </div>
              <p className={`text-2xl font-bold ${card.color} ${card.label === 'Top Categoria' ? 'text-sm' : ''}`}>{card.value}</p>
            </div>
          ))}
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-4 mb-4">
        <div className="flex gap-1 overflow-x-auto whitespace-nowrap">
          {LEVEL_TABS.map(tab => (
            <button
              key={tab.label}
              onClick={() => setLevelFilter(tab.value)}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                levelFilter === tab.value
                  ? 'bg-white/10 text-white'
                  : 'text-neutral-400 hover:text-white hover:bg-white/5'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
        <div className="flex flex-wrap items-center gap-2 ml-auto">
          <label className="text-xs text-neutral-500">De:</label>
          <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} className="bg-neutral-900 border border-white/10 rounded-lg px-3 py-1.5 text-xs text-white" />
          <label className="text-xs text-neutral-500">Até:</label>
          <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} className="bg-neutral-900 border border-white/10 rounded-lg px-3 py-1.5 text-xs text-white" />
          {(dateFrom || dateTo) && (
            <button onClick={() => { setDateFrom(''); setDateTo(''); }} className="text-xs text-neutral-500 hover:text-white">Limpar</button>
          )}
        </div>
      </div>

      {/* System Logs Table */}
      <div tabIndex={0} className="bg-neutral-900/50 border border-white/10 rounded-xl overflow-x-auto">
        <table className="w-full min-w-[680px] text-sm">
          <thead>
            <tr className="border-b border-white/10 text-neutral-400 text-left">
              <th className="p-4 w-8" />
              <th className="p-4">Timestamp</th>
              <th className="p-4">Usuário</th>
              <th className="p-4">Categoria</th>
              <th className="p-4">Nível</th>
              <th className="p-4">Mensagem</th>
            </tr>
          </thead>
          <tbody>
            {logsLoading ? (
              <tr><td colSpan={6} className="p-8 text-center"><div className="w-6 h-6 border-2 border-primary-500 border-t-transparent rounded-full animate-spin mx-auto" /></td></tr>
            ) : logs.length === 0 ? (
              <tr><td colSpan={6} className="p-8 text-center text-neutral-500">Nenhum log encontrado</td></tr>
            ) : (
              logs.map(log => (
                <React.Fragment key={log.id}>
                  <tr onClick={() => setExpandedLogId(expandedLogId === log.id ? null : log.id)} className="border-b border-white/5 hover:bg-white/5 cursor-pointer transition-colors">
                    <td className="pl-4">{expandedLogId === log.id ? <ChevronUp className="w-4 h-4 text-neutral-500" /> : <ChevronDown className="w-4 h-4 text-neutral-500" />}</td>
                    <td className="p-4 text-neutral-400" title={new Date(log.created_at).toLocaleString('pt-BR')}>{formatRelativeTime(log.created_at)}</td>
                    <td className="p-4 text-white">{log.user_email || <span className="text-neutral-500">Anônimo</span>}</td>
                    <td className="p-4 text-neutral-400 font-mono text-xs">{log.category}</td>
                    <td className="p-4">{levelBadge(log.level)}</td>
                    <td className="p-4 text-neutral-400" title={log.message}>{truncateMessage(log.message)}</td>
                  </tr>
                  {expandedLogId === log.id && <SystemLogDetail log={log} />}
                </React.Fragment>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {total > 0 && (
        <div className="flex items-center justify-between mt-4 text-sm text-neutral-400">
          <span>Mostrando {(page - 1) * PER_PAGE + 1}–{Math.min(page * PER_PAGE, total)} de {total}</span>
          <div className="flex gap-2">
            <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} className="p-1 rounded hover:bg-white/10 disabled:opacity-30"><ChevronLeft className="w-4 h-4" /></button>
            <span className="px-2">{page} / {totalPages}</span>
            <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages} className="p-1 rounded hover:bg-white/10 disabled:opacity-30"><ChevronRight className="w-4 h-4" /></button>
          </div>
        </div>
      )}
    </>
  );
};

// =====================================================
// MAIN COMPONENT — TABBED LAYOUT
// =====================================================

export const AdminLogs: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'webhooks' | 'system'>('webhooks');

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-white">Logs & Saúde</h1>
        <span className="flex items-center gap-1.5 text-xs text-neutral-500">
          <RefreshCw className="w-3 h-3 animate-spin" style={{ animationDuration: '3s' }} />
          Atualiza a cada 60s
        </span>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-6">
        <button
          onClick={() => setActiveTab('webhooks')}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
            activeTab === 'webhooks' ? 'bg-white/10 text-white' : 'text-neutral-400 hover:text-white hover:bg-white/5'
          }`}
        >
          Webhook Logs
        </button>
        <button
          onClick={() => setActiveTab('system')}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
            activeTab === 'system' ? 'bg-white/10 text-white' : 'text-neutral-400 hover:text-white hover:bg-white/5'
          }`}
        >
          System Logs
        </button>
      </div>

      {activeTab === 'webhooks' ? <WebhookLogsSection /> : <SystemLogsSection />}
    </div>
  );
};
